import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, FilePlus2, Settings, Plane, LogOut, BookOpen, Search, Scale, GitCompareArrows, CircleCheckBig, CalendarRange } from "lucide-react";
import { logout, fetchMe } from "../lib/api";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const navItems = [
  { to: "/", label: "Panel", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/nueva/adelanto", label: "Nueva · Adelanto", icon: FilePlus2, testid: "nav-new-advance" },
  { to: "/nueva/final", label: "Nueva · Final", icon: FilePlus2, testid: "nav-new-final" },
  { to: "/buscar", label: "Buscar cliente", icon: Search, testid: "nav-search" },
  { to: "/adelantos-por-ano", label: "Adelantos por año", icon: CalendarRange, testid: "nav-advances-by-year" },
  { to: "/costes", label: "Control de costes", icon: Scale, testid: "nav-cost-control" },
  { to: "/ajustes-importes", label: "Ajustes", icon: GitCompareArrows, testid: "nav-adjustments" },
  { to: "/cerrado-terminado", label: "Cerrado → Terminado", icon: CircleCheckBig, testid: "nav-cerrado-terminado" },
  { to: "/manual", label: "Manual", icon: BookOpen, testid: "nav-manual" },
  { to: "/ajustes", label: "Configuración", icon: Settings, testid: "nav-settings" },
];

export const Layout = () => {
  const loc = useLocation();
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const isPrint = loc.pathname.endsWith("/imprimir");

  useEffect(() => {
    if (isPrint) return;
    fetchMe().then(setUser).catch(() => {});
  }, [isPrint]);

  const handleLogout = async () => {
    try { await logout(); } catch {
      // Non-critical: server-side session cleanup may fail; client-side
      // navigation to /login still happens so the user is logged out.
    }
    toast.success("Sesión cerrada");
    nav("/login", { replace: true });
  };

  if (isPrint) return <Outlet />;

  return (
    <div className="min-h-screen bg-slate-50 font-plex text-slate-700">
      <div className="flex">
        <aside className="hidden md:flex md:flex-col w-64 min-h-screen bg-white border-r border-slate-200">
          <div className="px-6 py-7 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 bg-slate-900 text-white flex items-center justify-center rounded-md">
                <Plane size={18} strokeWidth={1.6} />
              </div>
              <div>
                <div className="font-cabinet font-bold text-slate-900 text-base leading-none">Viaja de Verdad</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mt-1">Facturación</div>
              </div>
            </div>
          </div>
          <nav className="px-3 py-4 flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm rounded-md transition-colors ${
                    isActive
                      ? "bg-slate-900 text-white"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`
                }
              >
                <item.icon size={16} strokeWidth={1.8} />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="mt-auto px-3 py-4 flex flex-col gap-2 border-t border-slate-100">
            {user && (
              <div className="px-2 py-1 text-[11px] text-slate-500 leading-tight">
                <div className="font-semibold text-slate-700 truncate">{user.name || user.email}</div>
                <div className="text-[10px] text-slate-400 truncate">{user.email}</div>
              </div>
            )}
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="flex items-center gap-3 px-3 py-2 text-xs rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"
            >
              <LogOut size={14} /> Cerrar sesión
            </button>
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400 px-2">v1 · EUR</div>
          </div>
        </aside>

        <main className="flex-1 min-w-0 min-h-screen">
          <header className="md:hidden border-b border-slate-200 bg-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 bg-slate-900 text-white flex items-center justify-center rounded">
                <Plane size={14} />
              </div>
              <span className="font-cabinet font-bold text-slate-900">Viaja de Verdad</span>
            </div>
          </header>
          <div className="px-4 sm:px-6 md:px-10 py-8 max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};
